import * as THREE from 'three';

/**
 * Advanced Clipping Module
 * Based on Three.js webgl_clipping_advanced example
 */

export interface ClippingConfig {
  enabled?: boolean;
  shadows?: boolean;
  visualize?: boolean;
  color?: number;
  shininess?: number;
  globalClipping?: boolean;
}

export class AdvancedClipping {
  private clipMaterial: THREE.MeshStandardMaterial;
  private object: THREE.InstancedMesh;
  private volumeVisualization: THREE.Group;
  private globalClippingPlanes: THREE.Plane[];
  private startTime: number;
  private transform: THREE.Matrix4;
  private tmpMatrix: THREE.Matrix4;
  private initialPosition: THREE.Vector3;

  private Planes: THREE.Plane[];
  private PlaneMatrices: THREE.Matrix4[];
  private GlobalClippingPlanes: THREE.Plane[];

  constructor(private renderer: THREE.WebGLRenderer, config: ClippingConfig = {}) {
    this.transform = new THREE.Matrix4();
    this.tmpMatrix = new THREE.Matrix4();
    this.startTime = Date.now();
    this.initialPosition = new THREE.Vector3();

    // Create clipping planes
    const Vertices = [
      new THREE.Vector3(+1, 0, +Math.SQRT1_2),
      new THREE.Vector3(-1, 0, +Math.SQRT1_2),
      new THREE.Vector3(0, +1, -Math.SQRT1_2),
      new THREE.Vector3(0, -1, -Math.SQRT1_2)
    ];

    const Indices = [
      0, 1, 2, 0, 2, 3, 0, 3, 1, 1, 3, 2
    ];

    this.Planes = this.planesFromMesh(Vertices, Indices);
    this.PlaneMatrices = this.Planes.map(p => this.planeToMatrix(p));
    this.GlobalClippingPlanes = this.cylindricalPlanes(5, 2.5);

    // Initialize material with better visual properties
    this.clipMaterial = new THREE.MeshStandardMaterial({
      color: config.color ?? 0xee0a10,
      roughness: 0.5,
      metalness: 0.3,
      side: THREE.DoubleSide,
      clippingPlanes: this.createPlanes(this.Planes.length),
      clipShadows: config.shadows ?? true,
      flatShading: false  // Smooth shading for better depth
    });

    // Create instanced mesh
    const count = 5 * 5 * 5;
    const geometry = new THREE.BoxGeometry(0.18, 0.18, 0.18);
    this.object = new THREE.InstancedMesh(geometry, this.clipMaterial, count);
    this.object.castShadow = true;
    this.object.receiveShadow = true;  // Receive shadows from other cubes

    let i = 0;
    const matrix = new THREE.Matrix4();
    const color = new THREE.Color();

    for (let z = -2; z <= 2; ++z) {
      for (let y = -2; y <= 2; ++y) {
        for (let x = -2; x <= 2; ++x) {
          matrix.setPosition(x / 5, y / 5, z / 5);
          this.object.setMatrixAt(i, matrix);

          // Set per-instance color with HSL for variety
          // Vary hue based on position to create color gradient
          const hue = (x + 2) / 4 * 0.15 + (y + 2) / 4 * 0.15 + (z + 2) / 4 * 0.15;
          const saturation = 0.7 + (Math.sin(i * 0.5) * 0.3);
          const lightness = 0.5 + (Math.cos(i * 0.3) * 0.2);
          color.setHSL(hue, saturation, lightness);
          this.object.setColorAt(i, color);

          i++;
        }
      }
    }

    // Need to update the instance matrix and colors
    this.object.instanceMatrix.needsUpdate = true;
    if (this.object.instanceColor) {
      this.object.instanceColor.needsUpdate = true;
    }

    // Create volume visualization
    this.volumeVisualization = this.createVolumeVisualization();
    this.volumeVisualization.visible = config.visualize ?? false;

    // Setup global clipping
    this.globalClippingPlanes = this.createPlanes(this.GlobalClippingPlanes.length);

    // Configure renderer
    renderer.localClippingEnabled = config.enabled ?? true;
    if (config.globalClipping) {
      renderer.clippingPlanes = this.globalClippingPlanes;
    }
  }

  private planesFromMesh(vertices: THREE.Vector3[], indices: number[]): THREE.Plane[] {
    const n = indices.length / 3;
    const result = new Array(n);

    for (let i = 0, j = 0; i < n; ++i, j += 3) {
      const a = vertices[indices[j]];
      const b = vertices[indices[j + 1]];
      const c = vertices[indices[j + 2]];

      result[i] = new THREE.Plane().setFromCoplanarPoints(a, b, c);
    }

    return result;
  }

  private createPlanes(n: number): THREE.Plane[] {
    const result = new Array(n);
    for (let i = 0; i !== n; ++i) {
      result[i] = new THREE.Plane();
    }
    return result;
  }

  private assignTransformedPlanes(planesOut: THREE.Plane[], planesIn: THREE.Plane[], matrix: THREE.Matrix4): void {
    for (let i = 0, n = planesIn.length; i !== n; ++i) {
      planesOut[i].copy(planesIn[i]).applyMatrix4(matrix);
    }
  }

  private cylindricalPlanes(n: number, innerRadius: number): THREE.Plane[] {
    const result = this.createPlanes(n);

    for (let i = 0; i !== n; ++i) {
      const plane = result[i];
      const angle = i * Math.PI * 2 / n;

      plane.normal.set(Math.cos(angle), 0, Math.sin(angle));
      plane.constant = innerRadius;
    }

    return result;
  }

  private planeToMatrix(plane: THREE.Plane): THREE.Matrix4 {
    const xAxis = new THREE.Vector3();
    const yAxis = new THREE.Vector3();
    const trans = new THREE.Vector3();
    const zAxis = plane.normal;
    const matrix = new THREE.Matrix4();

    // Hughes & Moeller '99
    // "Building an Orthonormal Basis from a Unit Vector."
    if (Math.abs(zAxis.x) > Math.abs(zAxis.z)) {
      yAxis.set(-zAxis.y, zAxis.x, 0);
    } else {
      yAxis.set(0, -zAxis.z, zAxis.y);
    }

    xAxis.crossVectors(yAxis.normalize(), zAxis);
    plane.coplanarPoint(trans);

    return matrix.set(
      xAxis.x, yAxis.x, zAxis.x, trans.x,
      xAxis.y, yAxis.y, zAxis.y, trans.y,
      xAxis.z, yAxis.z, zAxis.z, trans.z,
      0, 0, 0, 1
    );
  }

  private createVolumeVisualization(): THREE.Group {
    const planeGeometry = new THREE.PlaneGeometry(3, 3, 1, 1);
    const color = new THREE.Color();
    const volumeVisualization = new THREE.Group();

    for (let i = 0, n = this.Planes.length; i !== n; ++i) {
      const material = new THREE.MeshBasicMaterial({
        color: color.setHSL(i / n, 0.5, 0.5).getHex(),
        side: THREE.DoubleSide,
        opacity: 0.2,
        transparent: true,
        clippingPlanes: this.clipMaterial.clippingPlanes.filter((_plane: THREE.Plane, j: number) => j !== i)
      });

      const mesh = new THREE.Mesh(planeGeometry, material);
      mesh.matrixAutoUpdate = false;
      volumeVisualization.add(mesh);
    }

    return volumeVisualization;
  }

  /**
   * Add the clipping objects to the scene or a group
   */
  addToScene(parent: THREE.Object3D): void {
    parent.add(this.object);
    parent.add(this.volumeVisualization);
  }

  /**
   * Update the clipping animation
   */
  update(): void {
    const currentTime = Date.now();
    const time = (currentTime - this.startTime) / 1000;

    // Apply animation relative to initial position
    this.object.position.copy(this.initialPosition);
    this.object.position.y += 0.3; // Small vertical offset for animation
    this.object.rotation.x = time * 0.5;
    this.object.rotation.y = time * 0.2;

    this.object.updateMatrix();
    this.transform.copy(this.object.matrix);

    const bouncy = Math.cos(time * 0.5) * 0.5 + 0.7;
    this.transform.multiply(
      this.tmpMatrix.makeScale(bouncy, bouncy, bouncy)
    );

    this.assignTransformedPlanes(
      this.clipMaterial.clippingPlanes,
      this.Planes,
      this.transform
    );

    const planeMeshes = this.volumeVisualization.children;
    for (let i = 0, n = planeMeshes.length; i !== n; ++i) {
      this.tmpMatrix.multiplyMatrices(this.transform, this.PlaneMatrices[i]);
      this.setObjectWorldMatrix(planeMeshes[i] as THREE.Mesh, this.tmpMatrix);
    }

    this.transform.makeRotationY(time * 0.1);
    this.assignTransformedPlanes(
      this.globalClippingPlanes,
      this.GlobalClippingPlanes,
      this.transform
    );
  }

  private setObjectWorldMatrix(object: THREE.Object3D, matrix: THREE.Matrix4): void {
    const parent = object.parent;
    if (!parent) return;

    parent.updateMatrixWorld();
    object.matrix.copy(parent.matrixWorld).invert();
    object.applyMatrix4(matrix);
  }

  /**
   * Control visibility of volume visualization
   */
  setVisualizationVisible(visible: boolean): void {
    this.volumeVisualization.visible = visible;
  }

  /**
   * Enable/disable clipping shadows
   */
  setClipShadows(enabled: boolean): void {
    this.clipMaterial.clipShadows = enabled;
  }

  /**
   * Get the instanced mesh object for direct manipulation
   */
  getObject(): THREE.InstancedMesh {
    return this.object;
  }

  /**
   * Set the position of the clipping object
   */
  setPosition(x: number, y: number, z: number): void {
    this.initialPosition.set(x, y, z);
    this.object.position.set(x, y, z);
  }

  /**
   * Get the volume visualization group
   */
  getVolumeVisualization(): THREE.Group {
    return this.volumeVisualization;
  }
}
